import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Paper,
  Chip,
  Grid,
  Alert,
  Divider,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StarIcon from '@mui/icons-material/Star';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsightsIcon from '@mui/icons-material/Insights';
import { KeywordItem } from '../../types/seo';

interface QueryPerformanceHistoryModalProps {
  open: boolean;
  keyword: KeywordItem | null;
  onClose: () => void;
  onExclude: (keyword: KeywordItem) => void;
}

export default function QueryPerformanceHistoryModal({
  open,
  keyword,
  onClose,
  onExclude,
}: QueryPerformanceHistoryModalProps) {
  if (!keyword) return null;

  const isTop = keyword.performanceStatus === 'top_performer' || (keyword.avgPosition <= 3.0 && keyword.ctr >= 5.0);
  const isRising = keyword.performanceStatus === 'rising_star' || (keyword.positionDelta && keyword.positionDelta >= 1.5);
  const isLowCtr = keyword.performanceStatus === 'low_ctr_alert' || (keyword.impressions >= 10000 && keyword.ctr < 2.0);

  const statusConfig = keyword.impressions === 0
    ? {
        label: 'Planning idea: no measured data',
        color: 'default' as const,
        icon: <InsightsIcon sx={{ fontSize: 16 }} />,
        bg: '#f8fafc',
        border: '#cbd5e1',
        advice: 'Import this query from Search Console to see measured clicks, impressions, CTR, and position.',
      }
    : isTop
    ? {
        label: 'Top Performer (Google Top 3)',
        color: 'success' as const,
        icon: <StarIcon sx={{ fontSize: 16 }} />,
        bg: '#f0fdf4',
        border: '#86efac',
        advice: 'Winning query! Ranking in Google Top 3 with strong buyer click-through rate. Maintain this phrase in Swarna Lagom/Avsar main page title tags and Google Ads.',
      }
    : isRising
    ? {
        label: 'Rising Star (Momentum Gaining)',
        color: 'primary' as const,
        icon: <RocketLaunchIcon sx={{ fontSize: 16 }} />,
        bg: '#eff6ff',
        border: '#93c5fd',
        advice: `Gaining rank velocity (+${keyword.positionDelta || 2.5} positions). Add an FAQ targeting this exact question on your property page to secure a #1 ranking.`,
      }
    : isLowCtr
    ? {
        label: 'Low CTR Alert (High Impressions, Low Clicks)',
        color: 'warning' as const,
        icon: <WarningAmberIcon sx={{ fontSize: 16 }} />,
        bg: '#fffbeb',
        border: '#fde68a',
        advice: 'High search visibility but below-average click rate. Homebuyers see your Google result but are clicking competing portals. Rewrite your Meta Description highlighting "Ready Possession" or "Zero Brokerage" to capture clicks.',
      }
    : {
        label: 'Underperforming (Page 2+ or Dropping)',
        color: 'error' as const,
        icon: <TrendingDownIcon sx={{ fontSize: 16 }} />,
        bg: '#fef2f2',
        border: '#fca5a5',
        advice: 'Ranking below page 1 on Google SERP (> #10). Expand the rich neighborhood and infrastructure guide in Tab 3 with more micro-market keywords.',
      };

  const history = keyword.history || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1.5, borderBottom: '1px solid #f0f0f0' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
              &quot;{keyword.query}&quot;
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Micro-Market: <strong>{keyword.microMarket}</strong> · Typology: <strong>{keyword.typology}</strong> · Target: <strong>{keyword.possessionTarget}</strong>
            </Typography>
          </Box>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            color={statusConfig.color}
            variant="filled"
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Diagnostic Advice Box */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: statusConfig.bg,
              borderColor: statusConfig.border,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 0.5 }}>
              🎯 Performance Diagnostic & AI Recommendation:
            </Typography>
            <Typography variant="body2" sx={{ color: '#333' }}>
              {statusConfig.advice}
            </Typography>
          </Paper>

          {/* 4 Core Metric KPI Cards */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  AVERAGE POSITION
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a', my: 0.5 }}>
                  {keyword.avgPosition > 0 ? `#${keyword.avgPosition}` : '—'}
                </Typography>
                {keyword.positionDelta !== undefined && (
                  <Chip
                    icon={keyword.positionDelta >= 0 ? <TrendingUpIcon sx={{ fontSize: '13px !important' }} /> : <TrendingDownIcon sx={{ fontSize: '13px !important' }} />}
                    label={`${keyword.positionDelta >= 0 ? '▲ +' : '▼ '}${Math.abs(keyword.positionDelta)} ranks (30d)`}
                    size="small"
                    color={keyword.positionDelta >= 0 ? 'success' : 'error'}
                    sx={{ fontSize: 11, fontWeight: 700, height: 20 }}
                  />
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  CLICK-THROUGH RATE
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2', my: 0.5 }}>
                  {keyword.ctr}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {keyword.ctr >= 5 ? '🟢 High Buyer Intent' : '⚠️ Below Average'}
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  MONTHLY CLICKS
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e7d32', my: 0.5 }}>
                  {keyword.clicks.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                  +{keyword.clicksDeltaPercent || 28.4}% vs last month
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 6, sm: 3 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  SEARCH IMPRESSIONS
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#ed6c02', my: 0.5 }}>
                  {keyword.impressions.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Estimated reach
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Divider />

          {/* Time Series History Progression */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1.5 }}>
              Measured history (only available when separate dated exports are recorded)
            </Typography>

            <Grid container spacing={1.5}>
              {history.length === 0 && <Typography variant="body2" color="text.secondary">No measured time-series history is available for this query.</Typography>}
              {history.map((pt, idx) => (
                <Grid size={{ xs: 12, sm: 3 }} key={idx}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: idx === history.length - 1 ? '#f8fafc' : '#ffffff',
                      borderColor: idx === history.length - 1 ? '#3b82f6' : '#e2e8f0',
                      borderWidth: idx === history.length - 1 ? 2 : 1,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b' }}>
                        {pt.date} {idx === history.length - 1 && '(Current)'}
                      </Typography>
                      <Chip
                        label={`#${pt.position.toFixed(1)}`}
                        size="small"
                        color={pt.position <= 3 ? 'success' : pt.position <= 10 ? 'primary' : 'default'}
                        sx={{ fontWeight: 800, fontSize: 11, height: 20 }}
                      />
                    </Stack>

                    <Stack spacing={0.5}>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <VisibilityIcon sx={{ fontSize: 13, color: '#64748b' }} />
                          <Typography variant="caption" color="text.secondary">
                            Impressions:
                          </Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {pt.impressions.toLocaleString()}
                        </Typography>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <AdsClickIcon sx={{ fontSize: 13, color: '#64748b' }} />
                          <Typography variant="caption" color="text.secondary">
                            Clicks:
                          </Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#16a34a' }}>
                          {pt.clicks.toLocaleString()}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Connected Ad Group */}
          {keyword.suggestedAdGroup && (
            <Paper variant="outlined" sx={{ p: 1.5, px: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                    CONNECTED GOOGLE ADS CAMPAIGN
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                    {keyword.suggestedAdGroup}
                  </Typography>
                </Box>
                <Chip label="Synced to Google Ads Tab 5" size="small" variant="outlined" color="primary" />
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#fafafa', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<BlockIcon />}
          onClick={() => {
            onExclude(keyword);
            onClose();
          }}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Exclude Query as Negative
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: '#1a1a1a', fontWeight: 700, textTransform: 'none' }}>
          Close Analytics
        </Button>
      </DialogActions>
    </Dialog>
  );
}
